<template>
  <fullscreen-dialog
    v-model="model"
    :title="dialogTitle"
    icon="mdi-clipboard-check-outline"
    color="success"
    hide-footer
  >
    <template #app-bar-actions>
      <excel-button @click="exportExcel" class="mr-2" />
      <print-button @click="print" />
    </template>

    <v-form ref="form">
      <v-row>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="pb-0">
          <text-field
            :value="editedItem.warehouse?.name || ''"
            :label="$t('stock')"
            readonly
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2" class="pb-0">
          <text-field
            :value="localeDate(editedItem.date)"
            :label="$t('date')"
            readonly
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2" class="pb-0">
          <text-field
            v-model="searchText"
            :label="$t('search')"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" xl="2" class="pb-0">
          <autocomplete
            v-model="countFilter"
            :items="countFilterOptions"
            :label="$t('all_items')"
            item-text="text"
            item-value="value"
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="pb-0">
          <autocomplete
            v-model="differenceFilter"
            :items="differenceFilterOptions"
            :label="$t('difference')"
            item-text="text"
            item-value="value"
            hide-details
            clearable
          />
        </v-col>
      </v-row>
    </v-form>

    <div v-if="notes" class="notes-box mt-4 px-4 py-3 grey--text text--darken-3 body-1">
      <div class="font-weight-bold mb-2">{{ $t("notes") }}:</div>
      {{ notes }}
    </div>

    <!-- Read-only table -->
    <div class="pa-1">
      <v-data-table
        :headers="tableHeaders"
        :items="filteredGroceries"
        :loading="loadingItems"
        class="mt-4 striped-table ordera-box-shadow"
        disable-pagination
        hide-default-footer
      >
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }}</div>
          <div class="text-caption grey--text" v-if="item.code">{{ item.code }}</div>
        </template>
        <template v-slot:item.current_quantity="{ item }">
          {{ formatQuantity(item.current_quantity) }}
        </template>
        <template v-slot:item.new_quantity="{ item }">
          {{ formatQuantity(item.new_quantity) }}
        </template>
        <template v-slot:item.quantity="{ item }">
          <v-chip small :color="getDifferenceColor(item.quantity)" dark>
            {{ formatDifference(item.quantity) }}
          </v-chip>
        </template>
      </v-data-table>
    </div>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasExcel from "@/mixins/hasExcel";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import warehouseService from "@/services/warehouse";
import groceryService from "@/services/grocery";
const convert = require("cyrillic-to-latin");

export default {
  name: "WarehouseCompletedListDialog",
  emits: [],
  mixins: [hasModel, hasPrinting, hasSnackbar, hasExcel],
  components: {
    FullscreenDialog,
  },
  props: {
    listId: {
      type: [Number, String],
      default: null,
    },
    notes: {
      type: String,
      default: null,
    },
    completedAt: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loadingItems: false,
      internalGroceries: [],
      searchText: "",
      countFilter: null,
      differenceFilter: null,
      editedItem: {
        warehouse: null,
        date: "",
        name: "",
        groceries: [],
        documents: [],
      },
    };
  },
  methods: {
    resetData() {
      this.editedItem = {
        warehouse: null,
        date: "",
        name: "",
        groceries: [],
        documents: [],
      };
      this.internalGroceries = [];
      this.searchText = "";
      this.countFilter = null;
      this.differenceFilter = null;
    },
    async loadCompletedStocktaking() {
      if (!this.listId) return;

      try {
        this.loadingItems = true;

        // Fetch stocktaking details
        const detailsResponse = await warehouseService.getStocktakingDetails(this.listId);
        const details = detailsResponse.data?.data || detailsResponse.data || null;

        this.editedItem = { ...details };

        const countedGroceries = this.editedItem.groceries || [];

        // Fetch all groceries to get names and other details
        const groceriesResponse = await groceryService.getGroceries();
        const allGroceries = groceriesResponse.data?.data || groceriesResponse.data || [];
        const groceryMap = new Map(allGroceries.map((g) => [g.id, g]));

        const countedIds = new Set();

        const countedItems = countedGroceries.map((counted, index) => {
          const groceryId = counted.grocery_id || counted.id;
          const grocery = groceryMap.get(groceryId) || {};
          countedIds.add(groceryId);

          return {
            id: `counted_${index}_${groceryId}`,
            code: grocery.code || counted.code || "",
            name: grocery.name || counted.name || "",
            measure: grocery.measure || counted.measure || "",
            current_quantity: parseFloat(counted.system_stock) || 0,
            new_quantity: parseFloat(counted.physical_stock) || 0,
            quantity: parseFloat(counted.difference) || 0,
          };
        });

        const uncountedItems = allGroceries
          .filter((g) => !countedIds.has(g.id))
          .map((g, index) => ({
            id: `uncounted_${index}_${g.id}`,
            code: g.code || "",
            name: g.name || "",
            measure: g.measure || "",
            current_quantity: 0,
            new_quantity: 0,
            quantity: 0,
          }));

        this.internalGroceries = [...countedItems, ...uncountedItems];
      } catch (error) {
        console.error("Error loading completed stocktaking:", error);
        this.showErrorSnackbar(this.$t("error_loading_inventory"));
      } finally {
        this.loadingItems = false;
      }
    },
    formatQuantity(value) {
      if (value === null || value === undefined) return "-";
      return parseFloat(value).toFixed(2);
    },
    formatDifference(value) {
      const num = parseFloat(value) || 0;
      return num > 0 ? `+${num.toFixed(2)}` : num.toFixed(2);
    },
    getDifferenceColor(value) {
      const num = parseFloat(value) || 0;
      if (num > 0) return "success";
      if (num < 0) return "error";
      return "grey";
    },
    print() {
      this.printDocument({
        tables: [{ headers: this.printHeaders, items: this.printableItems }],
        title: this.$t("stocktaking") + " - " + (this.editedItem.name || ""),
        beforeTitleHtml: `
          <h2 style="margin-bottom: 0px">${this.$t("stock")}: ${this.toLatin(
          this.editedItem.warehouse?.name || ""
        )}</h2>
          <div style="font-size: 16px;margin-top: 20px;">
            ${this.$t("date")}: ${this.editedItem.date || ""}
          </div>
          <div style="font-size: 16px;margin-top: 10px;">
            ${this.$t("completed_at")}: ${this.completedDateTime}
          </div>
        `,
        hideDatetime: true,
      });
    },
    exportExcel() {
      const warehouseName = this.toLatin(this.editedItem.warehouse?.name || "Magacin");
      const stocktakingName = this.toLatin(this.editedItem.name || "Popis");

      this.downloadExcel({
        fileName: `${stocktakingName}_${this.editedItem.date || ""}.xlsx`,
        sheets: [
          {
            name: this.$t("stocktaking"),
            headers: this.printHeaders,
            items: this.printableItems,
            title: `${this.$t("stocktaking")} - ${stocktakingName}`,
            subtitle: `${this.$t("stock")}: ${warehouseName} | ${this.$t("date")}: ${
              this.editedItem.date || ""
            } | ${this.$t("completed_at")}: ${this.completedDateTime}`,
          },
        ],
      });
    },
  },
  computed: {
    countFilterOptions() {
      return [
        { text: this.$t("all_items"), value: null },
        { text: this.$t("counted"), value: "counted" },
        { text: this.$t("not_counted"), value: "not_counted" },
      ];
    },
    differenceFilterOptions() {
      return [
        { text: this.$t("all"), value: null },
        { text: this.$t("regular_state"), value: "zero" },
        { text: this.$t("surplus_on_list"), value: "surplus" },
        { text: this.$t("shortage_on_list"), value: "shortage" },
      ];
    },
    filteredGroceries() {
      let result = this.internalGroceries;

      if (this.searchText) {
        result = result.filter(
          (item) =>
            this.toLatin(item.name)
              .toLowerCase()
              .includes(this.toLatin(this.searchText).toLowerCase()) ||
            this.toLatin(item.code)
              .toLowerCase()
              .includes(this.toLatin(this.searchText).toLowerCase())
        );
      }

      if (this.countFilter === "counted") {
        result = result.filter((item) => item.new_quantity !== 0);
      } else if (this.countFilter === "not_counted") {
        result = result.filter((item) => item.new_quantity === 0);
      }

      if (this.differenceFilter === "zero") {
        result = result.filter((item) => parseFloat(item.quantity) === 0);
      } else if (this.differenceFilter === "surplus") {
        result = result.filter((item) => parseFloat(item.quantity) > 0);
      } else if (this.differenceFilter === "shortage") {
        result = result.filter((item) => parseFloat(item.quantity) < 0);
      }

      return result;
    },
    printableItems() {
      return this.internalGroceries.map((item, index) => ({
        num: index + 1,
        code: item.code || "",
        name: item.name,
        current_quantity: this.formatQuantity(item.current_quantity),
        new_quantity: this.formatQuantity(item.new_quantity),
        quantity: this.formatDifference(item.quantity),
        measure: item.measure,
      }));
    },
    dialogTitle() {
      return `${this.$t("stocktaking")} - ${this.editedItem.name || ""} (${this.$t(
        "completed"
      )})`;
    },
    completedDateTime() {
      return this.completedAt || "";
    },
    tableHeaders() {
      return [
        { text: this.$t("r_b"), value: "num", width: 80, sortable: false },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("book_state"), value: "current_quantity", align: "right" },
        {
          text: this.$t("inventory_state"),
          value: "new_quantity",
          align: "right",
          sortable: false,
        },
        { text: this.$t("difference"), value: "quantity", align: "right" },
        { text: this.$t("unit_of_measurment"), value: "measure", sortable: false },
      ];
    },
    printHeaders() {
      const headers = this.tableHeaders;
      headers.splice(2, 0, { text: this.$t("code"), value: "code" });
      return headers;
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.loadCompletedStocktaking();
      } else {
        this.resetData();
      }
    },
    listId(newVal) {
      if (newVal && this.model) {
        this.loadCompletedStocktaking();
      }
    },
  },
};
</script>
<style scoped>
.notes-box {
  background-color: #e8f5e9;
  border-left: 4px solid #4caf50;
  border-radius: 4px;
}
</style>
